import React, { useEffect, useRef, useState } from "react";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import {
  Invitation,
  formatRole,
  getRoleStyle
} from "../../utils/hooks/multi-tenant/invitation";
import { MdOutlineMail } from "react-icons/md";

interface InvitationsTableProps {
  invitations: Invitation[];
  isLoading: boolean;
  isError: boolean;
  availableRoles: { value: UserRole; label: string }[];
  onRoleChange: (invitationId: number, newRole: UserRole) => void;
  onDeleteClick: (id: number) => void;
  onResendInvitation: (email: string, role: UserRole) => void;
  externalUsersMap?: Record<string, any>;
}

const InvitationsTable: React.FC<InvitationsTableProps> = ({
  invitations,
  isLoading,
  isError,
  availableRoles,
  onRoleChange,
  onDeleteClick,
  onResendInvitation,
  externalUsersMap
}) => {
  const formatInvitationStatus = (status: string) => {
    const normalized = (status || "").toLowerCase();
    const map: Record<string, string> = {
      pending: "Pending",
      expired: "Expired",
      accepted: "Accepted"
    };
    return map[normalized] || (status ? status.charAt(0).toUpperCase() + status.slice(1) : "");
  };
  // Always-visible horizontal scrollbar (like Activity Logs)
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const update = () => {
      if (!scrollRef.current) return;
      setContentWidth(scrollRef.current.scrollWidth);
      setContainerWidth(scrollRef.current.clientWidth);
      setScrollLeft(scrollRef.current.scrollLeft);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [invitations]);

  const onTopScroll = () => {
    if (scrollRef.current) setScrollLeft(scrollRef.current.scrollLeft);
  };

  const thumbWidth = Math.max(
    containerWidth && contentWidth ? (containerWidth * containerWidth) / contentWidth : 0,
    40
  );
  const maxThumbLeft = Math.max(containerWidth - thumbWidth, 0);
  const thumbLeft = contentWidth > containerWidth
    ? (scrollLeft / (contentWidth - containerWidth)) * maxThumbLeft
    : 0;

  const onThumbDrag = (clientX: number) => {
    if (!scrollRef.current) return;
    const rect = scrollRef.current.getBoundingClientRect();
    const usable = containerWidth - thumbWidth;
    const rel = Math.min(Math.max(clientX - rect.left - thumbWidth / 2, 0), usable);
    const newLeft = (rel / usable) * (contentWidth - containerWidth);
    scrollRef.current.scrollLeft = newLeft;
    setScrollLeft(newLeft);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isDragging) onThumbDrag(e.clientX);
    };
    const onUp = () => setIsDragging(false);
    if (isDragging) {
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp, { once: true });
    }
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <div
        ref={scrollRef}
        onScroll={onTopScroll}
        className="overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
      <table className="min-w-[1200px] divide-y divide-gray-200">
        <thead className="bg-[#E5E7EB]">
          <tr>
            <th
              scope="col"
              className="px-8 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 w-[320px]"
            >
              User
            </th>
            <th
              scope="col"
              className="px-8 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 w-[200px]"
            >
              Manage role
            </th>
            <th
              scope="col"
              className="px-8 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 w-[160px]"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-8 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-700 w-[160px]"
            >
              Expires
            </th>
            <th
              scope="col"
              className="px-8 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-700 w-[220px]"
            >
              Role
            </th>
            <th
              scope="col"
              className="px-8 py-3 text-center text-xs font-medium uppercase text-gray-700 w-[140px]"
            >
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {isLoading ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-4 text-center text-sm text-gray-500"
              >
                Loading invitations...
              </td>
            </tr>
          ) : isError ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-4 text-center text-sm text-red-500"
              >
                Failed to load invitations
              </td>
            </tr>
          ) : invitations.length === 0 ? (
            <tr>
              <td
                colSpan={6}
                className="px-6 py-4 text-center text-sm text-gray-500"
              >
                No invitations found
              </td>
            </tr>
          ) : (
            invitations.map(invitation => (
              <tr
                key={invitation.id}
                className="transition-colors duration-200 hover:bg-gray-50"
              >
                <td className="px-8 py-4 text-sm text-gray-900">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const emailKey = (invitation.invitee_email || "").toLowerCase();
                      const extUser = externalUsersMap?.[emailKey];
                      const avatarSrc = invitation.Invitee?.pfp || extUser?.profilePicture || "/favicon.ico";
                      return (
                        <img
                          src={avatarSrc}
                          alt="User avatar"
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      );
                    })()}
                    <div>
                      <div className="font-medium text-gray-900">
                        {invitation.Invitee?.username || (() => {
                          const emailKey = (invitation.invitee_email || "").toLowerCase();
                          const extUser = externalUsersMap?.[emailKey];
                          return extUser?.displayName || extUser?.username || "";
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">{invitation.Invitee?.email || invitation.invitee_email}</div>
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap py-4 text-sm">
                  <select
                    className="rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    value={invitation.role}
                    onChange={e =>
                      onRoleChange(invitation.id, Number(e.target.value))
                    }
                  >
                    {availableRoles.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="whitespace-nowrap px-8 py-4 text-center">
                  <span
                    className={`rounded-full px-3 py-1 text-xs ${
                      invitation.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : invitation.status === "expired"
                        ? "bg-red-100 text-red-800"
                        : "bg-green-100 text-green-800"
                    }`}
                  >
                    {formatInvitationStatus(invitation.status)}
                  </span>
                </td>
                <td className="whitespace-nowrap px-8 py-4 text-sm text-gray-500">
                  {new Date(invitation.expires_at).toLocaleDateString()}
                </td>
                <td className="whitespace-nowrap px-8 py-4 text-center">
                  <span
                    className={`rounded-full px-3 py-1 text-xs ${getRoleStyle(
                      invitation.role
                    )}`}
                  >
                    {formatRole(invitation.role)}
                  </span>
                </td>
                <td className="whitespace-nowrap py-4 text-sm text-center">
                  {invitation.status === "expired" ? (
                    <button
                      onClick={() =>
                        onResendInvitation(
                          invitation.invitee_email,
                          invitation.role
                        )
                      }
                      className="inline-flex items-center justify-center text-blue-600 transition-colors hover:text-blue-900 focus:outline-none"
                      title="Resend invitation"
                    >
                      <MdOutlineMail className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => onDeleteClick(invitation.id)}
                      className="inline-flex items-center justify-center text-red-600 transition-colors hover:text-red-900 focus:outline-none"
                      title="Deactivate user"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      <div className="mt-2 h-3 bg-gray-200 rounded relative select-none mx-2">
        <div
          className="absolute top-0 h-3 bg-gray-400 rounded cursor-pointer"
          style={{ width: `${thumbWidth}px`, left: `${thumbLeft}px` }}
          onMouseDown={e => {
            e.preventDefault();
            setIsDragging(true);
          }}
        />
      </div>
    </div>
  );
};

export default InvitationsTable;
